import React, { useState } from 'react';
import { Facebook, Instagram, Check, X, Edit2, Send, Calendar, Image as ImageIcon, Film, Layers, RefreshCw, AlertCircle } from 'lucide-react';

export interface ContentSlot {
    id: number;
    channel: 'facebook' | 'instagram';
    scheduled_at: string | null;
    pillar: string | null;
    product_slug: string | null;
    angle_key: string | null;
    status: string;
    hook: string | null;
    copy: string;
    hashtags: string | null;
    cta: string | null;
    image_url: string | null;
    image_prompt: string | null;
    external_post_id: string | null;
    permalink: string | null;
    published_at: string | null;
    created_at: string;
    error_log: string | null;
    // Phase 1.5 multimedia
    format: 'post' | 'carousel' | 'reel' | 'banner' | 'story' | null;
    media_urls: string | null;
    media_status: 'pending' | 'generating' | 'ready' | 'failed' | null;
    media_error: string | null;
    reel_video_url: string | null;
    cover_image_url: string | null;
    narration_script: string | null;
    media_provider: string | null;
}

const PILLAR_COLORS: Record<string, string> = {
    DEMO: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    PROOF: 'bg-green-500/10 text-green-600 dark:text-green-400',
    EDUCATION: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    OFFER: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
};
const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-gray-500/10 text-gray-600',
    approved: 'bg-blue-500/10 text-blue-600',
    rendering: 'bg-yellow-500/10 text-yellow-600',
    ready: 'bg-cyan-500/10 text-cyan-600',
    published: 'bg-green-500/10 text-green-600',
    failed: 'bg-red-500/10 text-red-600',
    rejected: 'bg-gray-500/10 text-gray-500',
};
const MEDIA_STATUS_COLORS: Record<string, string> = {
    pending: 'bg-gray-300 text-gray-700',
    generating: 'bg-yellow-400 text-yellow-900 animate-pulse',
    ready: 'bg-green-400 text-green-900',
    failed: 'bg-red-400 text-red-900',
};
const FORMAT_LABELS: Record<string, { icon: React.ReactNode; label: string }> = {
    post:     { icon: <ImageIcon className="w-4 h-4" />, label: 'Post (1 obraz)' },
    carousel: { icon: <Layers className="w-4 h-4" />,    label: 'Carousel (3-5 obr.)' },
    reel:     { icon: <Film className="w-4 h-4" />,      label: 'Reel (15-30s wideo)' },
    banner:   { icon: <ImageIcon className="w-4 h-4" />, label: 'Banner (16:9)' },
    story:    { icon: <ImageIcon className="w-4 h-4" />, label: 'Story (24h)' },
};

interface Props {
    slot: ContentSlot;
    onAction?: (action: 'approved' | 'rejected' | 'updated' | 'published', slot: ContentSlot) => void;
}

function parseMediaUrls(slot: ContentSlot): string[] {
    if (slot.media_urls) {
        try { return JSON.parse(slot.media_urls); } catch { /* */ }
    }
    return slot.image_url ? [slot.image_url] : [];
}

export const SlotCard: React.FC<Props> = ({ slot, onAction }) => {
    const [editing, setEditing] = useState(false);
    const [copy, setCopy] = useState(slot.copy);
    const [scheduledAt, setScheduledAt] = useState(slot.scheduled_at || '');
    const [format, setFormat] = useState<string>(slot.format || 'post');
    const [working, setWorking] = useState<string | null>(null);

    const ChannelIcon = slot.channel === 'facebook' ? Facebook : Instagram;
    const mediaUrls = parseMediaUrls(slot);
    const isVideo = !!slot.reel_video_url;

    const action = async (path: string, body?: any) => {
        setWorking(path);
        try {
            const r = await fetch(`/api/admin/content/${slot.id}${path}`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: body ? JSON.stringify(body) : undefined,
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || data.detail || 'failed');
            return data;
        } finally { setWorking(null); }
    };

    const onApprove     = async () => { const d = await action('/approve');     onAction?.('approved', d.slot); };
    const onReject      = async () => { const d = await action('/reject', { reason: '' }); onAction?.('rejected', d.slot); };
    const onPublishNow  = async () => { const d = await action('/publish-now'); onAction?.('published', d.slot); };
    const onRegenMedia  = async () => { const d = await action('/regenerate-media'); onAction?.('updated', { ...slot, media_status: 'ready', media_urls: JSON.stringify(d.urls || []) } as ContentSlot); };

    const onSave = async () => {
        const r = await fetch(`/api/admin/content/${slot.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ copy, scheduled_at: scheduledAt, format }),
        });
        const data = await r.json();
        setEditing(false);
        onAction?.('updated', data.slot);
    };

    return (
        <div className="bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark p-4 space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <ChannelIcon className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
                    {slot.pillar && <span className={`px-2 py-0.5 rounded text-xs font-semibold ${PILLAR_COLORS[slot.pillar]}`}>{slot.pillar}</span>}
                    {slot.format && (
                        <span className="px-2 py-0.5 rounded text-xs bg-bkg-light dark:bg-bkg-dark inline-flex items-center gap-1">
                            {FORMAT_LABELS[slot.format]?.icon}
                            {slot.format}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {slot.media_status && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${MEDIA_STATUS_COLORS[slot.media_status] || ''}`} title="media status">
                            media: {slot.media_status}
                        </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[slot.status]}`}>{slot.status}</span>
                </div>
            </div>

            {/* Media preview */}
            {(mediaUrls.length > 0 || isVideo) && (
                <div className="rounded-lg overflow-hidden bg-bkg-light dark:bg-bkg-dark">
                    {isVideo ? (
                        <video src={slot.reel_video_url!} poster={slot.cover_image_url || undefined} controls className="w-full max-h-96 object-contain" />
                    ) : mediaUrls.length === 1 ? (
                        <img src={mediaUrls[0]} alt="" loading="lazy" className="w-full max-h-96 object-contain" />
                    ) : (
                        <div className="grid grid-cols-3 gap-1">
                            {mediaUrls.slice(0, 6).map((u, i) => (
                                <img key={i} src={u} alt="" loading="lazy" className="w-full aspect-square object-cover" />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {slot.media_status === 'failed' && slot.media_error && (
                <div className="text-xs text-red-600 bg-red-500/10 px-2 py-1 rounded flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> media gen: {slot.media_error.slice(0, 120)}
                </div>
            )}

            {/* Text content */}
            {slot.hook && <div className="font-semibold text-sm">{slot.hook}</div>}
            {!editing ? (
                <div className="text-sm whitespace-pre-wrap">{slot.copy}</div>
            ) : (
                <textarea value={copy} onChange={e => setCopy(e.target.value)} rows={5}
                    className="w-full p-2 border rounded-md text-sm bg-bkg-light dark:bg-bkg-dark" />
            )}
            {slot.hashtags && <div className="text-xs text-blue-600 dark:text-blue-400">{slot.hashtags}</div>}
            {slot.cta && <div className="text-xs italic">{slot.cta}</div>}

            {/* Format + scheduled (edit mode) */}
            {editing && (
                <div className="grid grid-cols-2 gap-2">
                    <select value={format} onChange={e => setFormat(e.target.value)}
                        className="px-2 py-1 border rounded-md text-sm bg-bkg-light dark:bg-bkg-dark">
                        {Object.entries(FORMAT_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                    <input type="datetime-local" value={scheduledAt.slice(0, 16)}
                        onChange={e => setScheduledAt(new Date(e.target.value).toISOString())}
                        className="px-2 py-1 border rounded-md text-sm bg-bkg-light dark:bg-bkg-dark" />
                </div>
            )}

            <div className="flex items-center gap-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                <Calendar className="w-3 h-3" />
                <span>{slot.scheduled_at ? new Date(slot.scheduled_at).toLocaleString('pl-PL') : 'unscheduled'}</span>
            </div>
            {slot.permalink && (
                <a href={slot.permalink} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-light dark:text-primary-dark underline">
                    Zobacz post →
                </a>
            )}
            {slot.error_log && <div className="text-xs text-red-500">⚠️ {slot.error_log}</div>}

            {/* Actions */}
            {slot.status === 'draft' && !editing && (
                <div className="flex gap-2 flex-wrap">
                    <button onClick={onApprove} disabled={!!working}
                        className="flex-1 px-3 py-1.5 text-sm bg-green-500/10 text-green-600 hover:bg-green-500/20 rounded-md font-semibold flex items-center justify-center gap-1">
                        <Check className="w-4 h-4" /> Zatwierdź
                    </button>
                    <button onClick={() => setEditing(true)} disabled={!!working}
                        className="px-3 py-1.5 text-sm bg-bkg-light dark:bg-bkg-dark hover:bg-border-light dark:hover:bg-border-dark rounded-md flex items-center gap-1">
                        <Edit2 className="w-4 h-4" /> Edytuj
                    </button>
                    <button onClick={onReject} disabled={!!working}
                        className="px-3 py-1.5 text-sm bg-red-500/10 text-red-600 hover:bg-red-500/20 rounded-md flex items-center gap-1">
                        <X className="w-4 h-4" /> Odrzuć
                    </button>
                </div>
            )}
            {editing && (
                <div className="flex gap-2">
                    <button onClick={onSave} className="flex-1 px-3 py-1.5 text-sm bg-primary-light dark:bg-primary-dark text-white rounded-md font-semibold">
                        Zapisz
                    </button>
                    <button onClick={() => { setEditing(false); setCopy(slot.copy); setFormat(slot.format || 'post'); }} className="px-3 py-1.5 text-sm bg-bkg-light dark:bg-bkg-dark rounded-md">
                        Anuluj
                    </button>
                </div>
            )}

            {/* Media regen button — visible always when slot is editable */}
            {(slot.status === 'draft' || slot.status === 'approved') && !editing && (
                <button onClick={onRegenMedia} disabled={working === '/regenerate-media'}
                    className="w-full px-3 py-1.5 text-xs bg-bkg-light dark:bg-bkg-dark hover:bg-border-light dark:hover:bg-border-dark rounded-md flex items-center justify-center gap-1">
                    <RefreshCw className={`w-3 h-3 ${working === '/regenerate-media' ? 'animate-spin' : ''}`} />
                    {slot.media_status === 'ready' ? 'Wygeneruj media ponownie' : 'Wygeneruj media (NVIDIA)'}
                </button>
            )}

            {slot.status === 'approved' && slot.media_status === 'ready' && (
                <button onClick={onPublishNow} disabled={!!working}
                    className="w-full px-3 py-1.5 text-sm bg-primary-light dark:bg-primary-dark text-white hover:opacity-90 rounded-md font-semibold flex items-center justify-center gap-1">
                    <Send className="w-4 h-4" /> Opublikuj teraz
                </button>
            )}
        </div>
    );
};
